import { NavLink, Link, useLocation } from 'react-router-dom';
import { useState, type ReactNode } from 'react';
import {
  LayoutDashboard,
  GaugeCircle,
  FileBarChart,
  IndianRupee,
  Users,
  Receipt,
  LogOut,
  Fuel,
  Settings,
  ShieldCheck,
  Compass,
  UserCheck,
  CreditCard,
  Package,
  Wallet,
  FileCheck2,
  MoreHorizontal,
  ChevronRight,
} from 'lucide-react';
import { useAuthStore } from '@/features/auth/store';
import { useLogout } from '@/features/auth/hooks';
import { useFeatures, type FeatureKey } from '@/features/features/hooks';
import { IstClock } from '@/components/IstClock';
import { Sheet } from '@/components/ui/Sheet';
import { cn } from '@/lib/cn';

/**
 * Owner/manager shell.
 *
 * Desktop (lg+): collapsible sidebar grouped into Operations / Finance /
 *   People & Ops / Inventory / Administration. Manager role hides
 *   owner-only groups (Fuel prices, Payroll, Samples, Setup, Audit).
 *
 * Mobile (<lg): 4 primary bottom-nav tabs (Dashboard · Shifts · Expenses
 *   · More). "More" opens a full-height bottom sheet with the rest of
 *   the destinations grouped the same way as desktop. This is the shape
 *   an owner-on-a-phone actually needs — the earlier "cram 14 icons
 *   into a horizontal scroller" approach hid everything past the third
 *   swipe.
 */

interface NavItem {
  to: string;
  label: string;
  Icon: React.ComponentType<{ className?: string }>;
  ownerOnly?: boolean;
  /** If set, hide the item when the tenant has this feature disabled. */
  feature?: FeatureKey;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Operations',
    items: [
      { to: '/', label: 'Dashboard', Icon: LayoutDashboard },
      { to: '/shifts', label: 'Shifts', Icon: GaugeCircle },
      { to: '/reports', label: 'Reports', Icon: FileBarChart },
    ],
  },
  {
    title: 'Finance',
    items: [
      // Owner-only per pilot request (2026-09-06): manager doesn't approve
      // expenses from the sidebar — they handle expenses via the shift page.
      { to: '/expenses', label: 'Expenses', Icon: Receipt, ownerOnly: true },
      { to: '/credit-customers', label: 'Credit', Icon: CreditCard, feature: 'credit' },
      { to: '/prices', label: 'Fuel prices', Icon: IndianRupee, ownerOnly: true },
      { to: '/payroll', label: 'Payroll', Icon: Wallet, ownerOnly: true, feature: 'payroll' },
    ],
  },
  {
    title: 'People',
    items: [
      { to: '/employees', label: 'Employees', Icon: Users },
      { to: '/attendance', label: 'Attendance', Icon: UserCheck },
    ],
  },
  {
    title: 'Inventory',
    items: [
      // Owner-only per pilot request (2026-09-06).
      { to: '/inventory', label: 'Inventory', Icon: Package, ownerOnly: true, feature: 'inventory' },
      { to: '/samples', label: 'Samples', Icon: FileCheck2, ownerOnly: true, feature: 'samples' },
    ],
  },
  {
    title: 'Administration',
    items: [
      // Setup is owner-only in general (payment methods, fuel types, machines,
      // shift templates, geo-fence) — but managers can manage tanks + record
      // deliveries. The Setup page detects role and shows the manager the
      // Tanks section only.
      { to: '/setup', label: 'Setup', Icon: Settings },
      { to: '/audit', label: 'Audit log', Icon: ShieldCheck, ownerOnly: true },
      { to: '/walkthrough', label: 'Walkthrough', Icon: Compass },
    ],
  },
];

// Owner mobile primary tabs. Managers drop Expenses (owner-only) and get
// Attendance in its place — managers deal with attendance/exceptions daily.
const OWNER_MOBILE_TABS: NavItem[] = [
  { to: '/', label: 'Dashboard', Icon: LayoutDashboard },
  { to: '/shifts', label: 'Shifts', Icon: GaugeCircle },
  { to: '/expenses', label: 'Expenses', Icon: Receipt },
];
const MANAGER_MOBILE_TABS: NavItem[] = [
  { to: '/', label: 'Dashboard', Icon: LayoutDashboard },
  { to: '/shifts', label: 'Shifts', Icon: GaugeCircle },
  { to: '/attendance', label: 'Attendance', Icon: UserCheck },
];

export function OwnerLayout({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const isOwner = user?.role === 'owner';
  const features = useFeatures();
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const visibleGroups = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter(
      (i) => (!i.ownerOnly || isOwner) && (!i.feature || features[i.feature]),
    ),
  })).filter((g) => g.items.length > 0);

  const MOBILE_TABS = isOwner ? OWNER_MOBILE_TABS : MANAGER_MOBILE_TABS;
  const MOBILE_TAB_ROUTES = new Set(MOBILE_TABS.map((t) => t.to));
  const moreActive = !MOBILE_TAB_ROUTES.has(pathname);

  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 border-r border-border bg-surface lg:block">
        <Link
          to="/"
          className="flex h-16 items-center gap-2 border-b border-border px-5"
        >
          <div className="grid h-8 w-8 place-items-center rounded-md bg-primary text-primary-foreground">
            <Fuel className="h-4 w-4" />
          </div>
          <div className="text-base font-semibold tracking-tight">FuelSync</div>
        </Link>
        <nav className="flex flex-col gap-5 p-4">
          {visibleGroups.map((g) => (
            <div key={g.title}>
              <div className="mb-1.5 px-2 text-2xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                {g.title}
              </div>
              <div className="space-y-0.5">
                {g.items.map((n) => (
                  <SidebarLink key={n.to} item={n} />
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      {/* Main pane */}
      <div className="min-w-0 flex-1 no-x-overflow">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-surface/95 px-4 backdrop-blur md:h-16 md:px-6">
          <div className="lg:hidden">
            <Link to="/" className="flex items-center gap-2">
              <div className="grid h-7 w-7 place-items-center rounded-md bg-primary text-primary-foreground">
                <Fuel className="h-3.5 w-3.5" />
              </div>
              <span className="text-sm font-semibold">FuelSync</span>
            </Link>
          </div>
          <div className="ml-auto flex min-w-0 items-center gap-2 md:gap-4">
            <div className="hidden md:block">
              <IstClock />
            </div>
            {user?.isPlatformAdmin && (
              <Link
                to="/admin/tenants"
                className="hidden md:inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary transition-colors hover:bg-primary/15"
                title="Platform admin console"
              >
                <ShieldCheck className="h-3.5 w-3.5" />
                Admin
              </Link>
            )}
            <Link
              to="/me/profile"
              className="min-w-0 rounded-md px-2 py-1 text-sm font-semibold transition-colors hover:bg-muted"
              title="My profile"
            >
              <span className="max-w-[140px] truncate sm:max-w-none">
                {user?.fullName?.split(' ')[0] ?? '—'}
              </span>
            </Link>
            <button
              onClick={() => logout.mutate()}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </header>

        <main className="p-4 pb-24 md:p-6 lg:pb-8">{children}</main>
      </div>

      {/* Mobile bottom nav — 4 tabs, safe-area padded */}
      <nav
        className="fixed inset-x-0 bottom-0 z-40 flex border-t border-border bg-surface/95 pb-safe backdrop-blur lg:hidden"
        aria-label="Primary"
      >
        {MOBILE_TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.to === '/'}
            className={({ isActive }) =>
              cn(
                'flex flex-1 flex-col items-center gap-0.5 py-2.5 text-2xs font-medium transition-colors',
                'min-h-[56px]',
                isActive
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground',
              )
            }
          >
            <t.Icon className="h-5 w-5" />
            {t.label}
          </NavLink>
        ))}
        <button
          onClick={() => setMoreOpen(true)}
          className={cn(
            'flex flex-1 flex-col items-center gap-0.5 py-2.5 text-2xs font-medium transition-colors',
            'min-h-[56px]',
            moreActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
          )}
          aria-label="More"
        >
          <MoreHorizontal className="h-5 w-5" />
          More
        </button>
      </nav>

      {/* "More" bottom sheet */}
      <Sheet open={moreOpen} onClose={() => setMoreOpen(false)} title="More">
        <div className="flex flex-col gap-5">
          {visibleGroups
            // Hide items already in the primary bottom bar.
            .map((g) => ({ ...g, items: g.items.filter((i) => !MOBILE_TAB_ROUTES.has(i.to)) }))
            .filter((g) => g.items.length > 0)
            .map((g) => (
              <div key={g.title}>
                <div className="mb-2 text-2xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  {g.title}
                </div>
                <div className="grid gap-1">
                  {g.items.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      onClick={() => setMoreOpen(false)}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 rounded-md border border-transparent px-3 py-3 text-sm font-medium transition-colors',
                          isActive
                            ? 'border-primary-border bg-primary-soft text-primary'
                            : 'text-foreground hover:bg-muted',
                        )
                      }
                    >
                      <n.Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="flex-1">{n.label}</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </Sheet>
    </div>
  );
}

function SidebarLink({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-primary text-primary-foreground shadow-sm'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
        )
      }
    >
      <item.Icon className="h-4 w-4" />
      {item.label}
    </NavLink>
  );
}
